import * as React from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import '@/config/app';
import { mixpanel } from '@/api/app/mixpanel';
import { Honeybadger } from '@/api/app/honeybadger';
import { store, persistor } from '@/redux/app/store';
import type { UserSettings, AccountSettings } from '@/redux/app/reducers/settings';
import { AppNavigator } from '@/lib/navigation';

import { AppSecretsContext } from './subcomponents/secrets/app-secrets';
import { UserInfo, UserInfoContext } from './subcomponents/user/user-info';
import { AccountInfo, AccountInfoContext } from './subcomponents/account/account-info';
import { Root } from './subcomponents/Root';
import { createPane } from '../lib/navigation/create-pane';
import { TicketView, EmptyTicket } from './subcomponents/ticket';
import { Inbox } from './subcomponents/inbox';
import { Onboarding } from './subcomponents/onboarding/Onboarding';
import { Billing } from './subcomponents/billing/Billing';
import { CheckoutSuccess } from './subcomponents/billing/CheckoutSuccess';
import { DefaultError } from './subcomponents/errors/DefaultError';

// #region Dev Setup
declare global {
  interface Window {
    ReactRailsUJS: {
      mountComponents: () => void;
      unmountComponents: () => void;
    };
    enableDevHelpers: () => void;
    whyDidYouRender: (options: Record<string, any>) => void;
  }
}
window.enableDevHelpers = () => {
  /* eslint-disable global-require */
  require('@/redux/app/dev-setup');
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  window.whyDidYouRender = (options) => {
    window.ReactRailsUJS?.unmountComponents();
    whyDidYouRender(React, {
      trackAllPureComponents: true,
      ...options,
    });
    window.ReactRailsUJS?.mountComponents();
  };
  /* eslint-enable global-require */
};
if (process.env.NODE_ENV === 'development') window.enableDevHelpers();
// #endregion Dev Setup

// #region Route Definitions

const IndexPane = createPane('IndexPane', {
  'accounts/:subdomain/checkout_success': CheckoutSuccess,
  'accounts/:subdomain/billing_v2': Billing,
  'accounts/:subdomain/onboarding': Onboarding,
  'accounts/:subdomain/inbox': Inbox,
  'accounts/:subdomain/tickets': Inbox,
  'accounts/:subdomain/tickets/:ticket': Inbox,
});

const DetailsPane = createPane('DetailsPane', {
  'accounts/:subdomain/tickets': EmptyTicket,
  'accounts/:subdomain/tickets/:ticket': TicketView,
});
// #endregion Route Definitions

// #region App Component
export interface AppProps {
  path: string;
  params: Record<string, string>;
  userSettings: UserSettings;
  accountSettings: AccountSettings;
  userInfo: UserInfo;
  accountInfo: AccountInfo;
  secrets: Record<string, string>;
  userAgent: Record<string, string>;
}

export interface AppState {
  errorsPresent: boolean;
}

export class App extends React.Component<AppProps, AppState> {
  constructor(props: AppProps) {
    super(props);
    this.state = { errorsPresent: false };
    mixpanel.register(props.userAgent);
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Honeybadger.notify(error, { context: info });
    this.setState({ errorsPresent: true });
  }

  render() {
    const { errorsPresent } = this.state;
    const {
      path, params, userSettings, accountSettings, userInfo, accountInfo, secrets,
    } = this.props;

    return (
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>

          <AppSecretsContext.Provider value={secrets}>
            <UserInfoContext.Provider value={userInfo}>
              <AccountInfoContext.Provider value={accountInfo}>

                <Root
                  accountId={accountInfo.id}
                  accountSettings={accountSettings}
                  userSettings={userSettings}
                >
                  {!errorsPresent && (
                    <AppNavigator
                      defaultPath={path}
                      defaultParams={params}
                    >
                      <IndexPane primary ErrorComponent={DefaultError} />
                      <DetailsPane ErrorComponent={DefaultError} />
                    </AppNavigator>
                  )}
                  {errorsPresent && <DefaultError />}
                </Root>

              </AccountInfoContext.Provider>
            </UserInfoContext.Provider>
          </AppSecretsContext.Provider>

        </PersistGate>
      </Provider>
    );
  }
}

export default App;
// #endregion App Component
